import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { CanActivateAccessTokenGuard } from '@features/auth/public-api';
import { redirectCongSvMoiGuard } from './core/guards/redirect-cong-sv-moi.guard';

const routes: Routes = [
  /**
   * Trang chủ: Student Workspace.
   *
   * Có ĐỊA CHỈ RIÊNG `/workspace` chứ không nấp sau path rỗng — giao diện chỉ
   * có MỘT địa chỉ, chia sẻ link ra thì ai cũng vào đúng một chỗ.
   *
   * PHẢI đứng TRƯỚC MemLayoutModule: MemLayout gắn ở path rỗng nên nó khớp mọi
   * URL, khai sau thì `workspace` bị nuốt trước.
   *
   * Dùng `loadComponent` chứ không `loadChildren`: workspace là một màn duy
   * nhất, không có route con.
   */
  {
    path: 'workspace',
    /**
     * ⚠ `redirectCongSvMoiGuard` đứng TRƯỚC — S03 đưa Workspace sang cổng sinh
     * viên mới, địa chỉ này chỉ còn là lối chuyển tiếp. Đứng trước thì người chưa
     * đăng nhập cũng được đẩy thẳng sang, khỏi phải đăng nhập vào app cũ chỉ để
     * bật ra ngoài. Xem ghi chú trong guard.
     */
    canActivate: [redirectCongSvMoiGuard, CanActivateAccessTokenGuard],
    loadComponent: () =>
      import('./layouts/workspace-layout/workspace-layout.component').then(
        m => m.WorkspaceLayoutComponent
      ),
  },
  /**
   * `/dashboard` — giao diện cũ — NAY DẪN VỀ WORKSPACE.
   *
   * Giao diện cũ không còn là một điểm đến; mọi lối vào nó đổ hết về giao diện
   * mới. Redirect đặt Ở ĐÂY, trước MemLayoutModule, để `/dashboard` khỏi phải
   * tải chunk mem-layout rồi mới quay đầu.
   *
   * Cố ý để `pathMatch` mặc định (`'prefix'`), KHÔNG dùng `'full'`: bên trong
   * `DashboardRoutingModule` còn một lối `/dashboard/dashboard`, khớp đúng
   * `'full'` là chừa lại cửa sau đó. Phần đuôi thừa được router ghép vào đích
   * (`/workspace/dashboard`), không khớp gì nên rơi xuống `**` và về đúng
   * `/workspace`.
   *
   * Query string được giữ nguyên qua redirect, nên đường đăng nhập bằng token
   * `/dashboard?token=…&redirect=…` vẫn vào được.
   *
   * ↩ Muốn trả giao diện cũ về chỗ cũ: XOÁ ĐÚNG route này — mục `dashboard`
   * trong `mem-layout.routing.module.ts` vẫn còn nguyên, không phải dựng lại.
   */
  { path: 'dashboard', redirectTo: 'workspace' },
  /**
   * Vào gốc thì đưa về địa chỉ chính thức của giao diện mới.
   *
   * Redirect thay vì gắn thêm component ở đây, để workspace chỉ có MỘT địa chỉ
   * — hai URL cùng render một màn thì chia sẻ link ra sẽ mỗi người một kiểu.
   * Các nơi điều hướng `router.navigate([''])` sau đăng nhập nhờ vậy vẫn đúng.
   */
  { path: '', pathMatch: 'full', redirectTo: 'workspace' },
  /**
   * Thiết lập path và import các module phát triển trong tương lai vào
   */
  {
    path: '',
    loadChildren: () => import('./layouts/mem-layout/mem-layout.module').then(m => m.MemLayoutModule),
  },
  {
    path: '',
    loadChildren: () => import('./layouts/guest-layout/guest-layout.module').then(m => m.GuestLayoutModule),
  },

  /**
   * Đường dẫn không khớp gì → về trang chủ mới.
   *
   * Trỏ thẳng `workspace` thay vì `''` để không phải đi qua hai lần chuyển tiếp.
   *
   * ⚠ Wildcard này cũng là chỗ NUỐT LỖI: route nào cấu hình sai sẽ không báo
   * "Cannot match any routes" mà lặng lẽ quay về workspace, đọc ra như thể
   * trang đó không tồn tại. Thêm route mới thì kiểm bằng cách gõ thẳng URL.
   */
  { path: '**', redirectTo: 'workspace' },
]

@NgModule({
  imports: [
    RouterModule.forRoot(
      routes,
      {
        // preloadingStrategy: PreloadAllModules,//Tự động load toàn bộ các module khác khi run xong các phần chính
        // useHash: true
      },
    )
  ],
  providers: [
    { provide: LocationStrategy, useClass: PathLocationStrategy },
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
